import type {
  SeedCampaignDetail,
  SeedCampaignDetailResponse,
  SeedPreview,
  SeedPreviewResponse,
  SeedTemplate,
  SeedTemplateDateFormat,
  SeedTemplateField,
  SeedTemplateFieldResponse,
  SeedTemplateFieldType,
  SeedTemplateResponse,
} from '../types';

export function seedTemplateMapper(response: SeedTemplateResponse): SeedTemplate {
  return {
    key: response.key,
    label: response.label,
    // Null is a real state: this environment has no id for that name, so no run can send it. The
    // picker says so rather than hiding the entry.
    templateId: response.template_id ?? null,
    sender: response.sender ?? null,
    // `required` and the list itself stay straight through, with no default: the route always
    // emits both, and every default available here falls in the unblocking direction, so a
    // malformed answer would send a template with a substitution left empty instead of refusing.
    //
    // The presentation keys are the opposite case and they ARE defaulted, because a bundle and an
    // API do not deploy together: a field whose label, placeholder or type has not arrived has to
    // render as a working text input under its own key, never as a blank row.
    dynamicFields: response.dynamic_fields.map(seedTemplateFieldMapper),
  };
}

function seedTemplateFieldMapper(field: SeedTemplateFieldResponse): SeedTemplateField {
  const format = narrowDateFormat(field.format);

  return {
    name: field.name,
    label: field.label?.trim() || field.name,
    type: narrowFieldType(field.type),
    placeholder: field.placeholder ?? '',
    required: field.required,
    ...(field.values ? { values: [...field.values] } : {}),
    ...(format ? { format } : {}),
    ...(field.nullable ? { nullable: field.nullable } : {}),
    ...(field.constant !== undefined ? { constant: field.constant } : {}),
  };
}

const FIELD_TYPES: readonly SeedTemplateFieldType[] = [
  'string',
  'number',
  'currency',
  'date',
  'datetime',
  'url',
  'enum',
  'boolean',
  'json',
];

const DATE_FORMATS: readonly SeedTemplateDateFormat[] = ['us_slash', 'long_day', 'long_day_padded', 'iso_datetime'];

/**
 * A type this bundle knows, or `string`.
 *
 * Both directions of the deploy gap land here: an older API sends no type at all,
 * and an API ahead of this bundle sends one it has never heard of. A text input renders either,
 * and the value still reaches the run; guessing a control from an unknown name would not.
 */
function narrowFieldType(type: string | undefined): SeedTemplateFieldType {
  return FIELD_TYPES.find((known) => known === type) ?? 'string';
}

function narrowDateFormat(format: string | undefined): SeedTemplateDateFormat | undefined {
  return DATE_FORMATS.find((known) => known === format);
}

export function seedTemplateListMapper(response: SeedTemplateResponse[]): SeedTemplate[] {
  return response.map(seedTemplateMapper);
}

export function seedCampaignMapper(response: SeedCampaignDetailResponse): SeedCampaignDetail {
  return {
    id: response.id,
    name: response.name,
    slug: response.slug,
    subject: response.subject ?? null,
    previewText: response.preview_text ?? null,
    senderFrom: response.sender_from ?? null,
    status: response.status ?? null,
    // Null is a real state: the campaign has never been sent. The picker says so.
    lastSentAt: response.last_sent_at ?? null,
    // A non-string in the list would reach `parseToken` and throw on `.startsWith`. The server
    // sends strings; this is the boundary that makes that true for the UI whatever arrives.
    tokens: (response.tokens ?? []).filter((token): token is string => typeof token === 'string'),
    tokensTruncated: Boolean(response.tokens_truncated),
    catalogs: (response.catalogs ?? []).map((catalog) => ({
      slug: catalog.slug,
      catalogId: catalog.catalog_id,
      name: catalog.name,
      fields: catalog.fields ?? [],
    })),
    reservedSegment: response.reserved_segment
      ? {
          slug: response.reserved_segment.slug,
          name: response.reserved_segment.name,
          catalogSlugs: response.reserved_segment.catalog_slugs ?? [],
        }
      : null,
    hasMagicLoginPlaceholder: Boolean(response.has_magic_login_placeholder),
  };
}

export function seedPreviewMapper(response: SeedPreviewResponse): SeedPreview {
  return {
    from: response.from ?? null,
    to: response.to ?? null,
    subject: response.subject ?? null,
    previewText: response.preview ?? null,
    html: response.html ?? null,
    // The server decides this, and the client never infers it from an empty body: a template with
    // no body at all is still a rendered email, and a reason is what tells the two apart.
    isRendered: Boolean(response.is_rendered),
    unavailableReason: response.unavailable_reason ?? null,
    // Null for every campaign type, and the two fields the transactional card draws instead of a
    // body. The server decides which: it answers `is_rendered: true` with no body for a template.
    templateId: response.template_id ?? null,
    dynamicTemplateData: response.dynamic_template_data ?? null,
  };
}
